import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Modal from "../Modal/Modal";
import "../../Styles/Seller/SellerRegisterForm.css";
import { API_URL } from "../../utils/Api";
import { sellerAgreementContent } from "../../constants/sellerAgreementContent";

export default function SellerRegisterForm({ onClose }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    businessName: "",
    password: "",
    confirmPassword: "",
  });

  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [hasAgreedSellerAgreement, setHasAgreedSellerAgreement] = useState(false);
  const [sellerSignatureDataUrl, setSellerSignatureDataUrl] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);

  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpTimer, setOtpTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const signatureCanvasRef = useRef(null);
  const signatureDrawingRef = useRef(false);
  const signatureContextRef = useRef(null);
  const lastSignaturePointRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((state) => ({ ...state, [name]: value }));

    if (submitStatus.message) {
      setSubmitStatus({ type: "", message: "" });
    }

    if (name === "password" || name === "confirmPassword") {
      if (name === "password") {
        setPasswordMatch(value === form.confirmPassword);
      } else {
        setPasswordMatch(value === form.password);
      }
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const nextOtp = [...otp];
    nextOtp[index] = value;
    setOtp(nextOtp);

    if (value !== "" && index < 5) {
      const nextInput = document.getElementById(`seller-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      const previousInput = document.getElementById(`seller-otp-${index - 1}`);
      if (previousInput) previousInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);

    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.split("");
      const nextOtp = [...otp];
      digits.forEach((digit, index) => {
        if (index < 6) nextOtp[index] = digit;
      });
      setOtp(nextOtp);
    }
  };

  const startOtpTimer = () => {
    setOtpTimer(60);
    setCanResend(false);

    const timer = setInterval(() => {
      setOtpTimer((previous) => {
        if (previous <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }

        return previous - 1;
      });
    }, 1000);
  };

  const validateForm = () => {
    if (
      !form.fullName ||
      !form.email ||
      !form.phone ||
      !form.businessName ||
      !form.password ||
      !form.confirmPassword
    ) {
      setSubmitStatus({
        type: "error",
        message: "Please fill in all fields",
      });
      return false;
    }

    if (form.password !== form.confirmPassword) {
      setSubmitStatus({
        type: "error",
        message: "Passwords do not match",
      });
      return false;
    }

    return true;
  };

  const sendSellerOtp = async () => {
    setIsSubmitted(true);
    setSubmitStatus({ type: "info", message: "Sending OTP to your email..." });

    try {
      const sellerData = {
        name: form.fullName,
        email: form.email,
        phone: form.phone,
        company: form.businessName,
        password: form.password,
        sellerAgreementAccepted: true,
        sellerSignature: sellerSignatureDataUrl,
      };

      const response = await axios.post(
        `${API_URL.api}/registerSeller`,
        sellerData,
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to send OTP");
      }

      setSubmitStatus({
        type: "success",
        message: "OTP sent successfully! Please check your email.",
      });
      setShowOTP(true);
      setRegisteredEmail(form.email);
      startOtpTimer();

      setForm({
        fullName: "",
        email: "",
        phone: "",
        businessName: "",
        password: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Registration error:", error);
      setSubmitStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Registration failed. Please try again.",
      });
    } finally {
      setIsSubmitted(false);
    }
  };

  const openTermsModal = () => {
    setHasAgreedSellerAgreement(false);
    setShowTermsModal(true);
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
    setHasAgreedSellerAgreement(false);
  };

  useEffect(() => {
    if (!showTermsModal) {
      return undefined;
    }

    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return undefined;
    }

    let isDisposed = false;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    const context = canvas.getContext("2d");

    if (!context || !rect.width || !rect.height) {
      return undefined;
    }

    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(ratio, ratio);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 2.5;
    context.strokeStyle = "#0f172a";
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, rect.width, rect.height);
    signatureContextRef.current = context;

    if (!sellerSignatureDataUrl) {
      return undefined;
    }

    const previewImage = new Image();
    previewImage.onload = () => {
      if (isDisposed || !signatureContextRef.current) {
        return;
      }

      signatureContextRef.current.fillStyle = "#ffffff";
      signatureContextRef.current.fillRect(0, 0, rect.width, rect.height);
      signatureContextRef.current.drawImage(previewImage, 0, 0, rect.width, rect.height);
    };
    previewImage.src = sellerSignatureDataUrl;

    return () => {
      isDisposed = true;
    };
  }, [sellerSignatureDataUrl, showTermsModal]);

  const getSignaturePoint = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const persistSignaturePreview = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) {
      return;
    }

    setSellerSignatureDataUrl(canvas.toDataURL("image/png"));
  };

  const startSignature = (event) => {
    const context = signatureContextRef.current;
    const canvas = signatureCanvasRef.current;
    const point = getSignaturePoint(event);

    if (!context || !canvas || !point) {
      return;
    }

    signatureDrawingRef.current = true;
    lastSignaturePointRef.current = point;
    canvas.setPointerCapture?.(event.pointerId);

    context.fillStyle = "#0f172a";
    context.beginPath();
    context.arc(point.x, point.y, 1.4, 0, Math.PI * 2);
    context.fill();
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const drawSignature = (event) => {
    if (!signatureDrawingRef.current) {
      return;
    }

    const context = signatureContextRef.current;
    const point = getSignaturePoint(event);
    const previousPoint = lastSignaturePointRef.current;

    if (!context || !point || !previousPoint) {
      return;
    }

    context.beginPath();
    context.moveTo(previousPoint.x, previousPoint.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    lastSignaturePointRef.current = point;
  };

  const endSignature = (event) => {
    const canvas = signatureCanvasRef.current;

    if (!signatureDrawingRef.current) {
      return;
    }

    signatureDrawingRef.current = false;
    lastSignaturePointRef.current = null;
    canvas?.releasePointerCapture?.(event.pointerId);
    persistSignaturePreview();
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const context = signatureContextRef.current;

    if (!canvas || !context) {
      setSellerSignatureDataUrl("");
      return;
    }

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = canvas.width / ratio;
    const height = canvas.height / ratio;

    context.fillStyle = "#ffffff";
    context.clearRect(0, 0, width, height);
    context.fillRect(0, 0, width, height);
    setSellerSignatureDataUrl("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!termsAccepted) {
      openTermsModal();
      return;
    }

    await sendSellerOtp();
  };

  const handleAcceptTerms = async () => {
    if (!hasAgreedSellerAgreement) {
      setSubmitStatus({
        type: "error",
        message: "Please agree to the Seller Terms & Conditions to continue.",
      });
      return;
    }

    if (!sellerSignatureDataUrl) {
      setSubmitStatus({
        type: "error",
        message: "Please add your signature before accepting the agreement.",
      });
      return;
    }

    setTermsAccepted(true);
    closeTermsModal();
    await sendSellerOtp();
  };

  const handleVerifyOtp = async () => {
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      setSubmitStatus({
        type: "error",
        message: "Please enter complete 6-digit OTP",
      });
      return;
    }

    setIsVerifying(true);
    setSubmitStatus({ type: "info", message: "Verifying OTP..." });

    try {
      const response = await axios.post(`${API_URL.api}/verifyotp1`, {
        email: registeredEmail,
        otp: otpString,
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(response.data.message || "Invalid OTP");
      }

      setSubmitStatus({
        type: "success",
        message:
          "Email verified successfully! Registration completed. We will contact you soon.",
      });

      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }

      setTimeout(() => {
        onClose?.();
      }, 2000);
    } catch (error) {
      console.error("OTP verification error:", error);
      setSubmitStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          "OTP verification failed. Please try again.",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToRegistration = () => {
    setShowOTP(false);
    setOtp(["", "", "", "", "", ""]);
    setSubmitStatus({ type: "", message: "" });
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const renderAgreementBlock = (block, sectionTitle, blockIndex) => {
    if (block.type === "list") {
      return (
        <ul
          key={`${sectionTitle}-list-${blockIndex}`}
          className="seller-terms-list"
        >
          {block.items.map((item) => (
            <li key={`${sectionTitle}-${item}`}>{item}</li>
          ))}
        </ul>
      );
    }

    if (block.type === "link") {
      return (
        <p
          key={`${sectionTitle}-link-${blockIndex}`}
          className="seller-terms-paragraph"
        >
          {block.prefix}
          <a
            className="seller-terms-link"
            href={block.href}
            target="_blank"
            rel="noreferrer"
          >
            {block.text}
          </a>
        </p>
      );
    }

    return (
      <p
        key={`${sectionTitle}-paragraph-${blockIndex}`}
        className="seller-terms-paragraph"
      >
        {block.text}
      </p>
    );
  };

  return (
    <div className="seller-form-wrapper">
      <div className="seller-form-container">
        <button className="seller-close-btn" onClick={onClose}>
          &times;
        </button>

        <div className="seller-form-header">
          <div className="seller-form-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                fill="white"
              />
            </svg>
          </div>
          <h2 className="seller-form-title">
            {showOTP ? "Verify Email" : "Become a Seller"}
          </h2>
          <p className="seller-form-subtitle">
            {showOTP
              ? `We've sent a verification code to ${registeredEmail}`
              : "Join our marketplace and start selling today"}
          </p>
        </div>

        {submitStatus.message && (
          <div className={`seller-status-message ${submitStatus.type}`}>
            {submitStatus.type === "success" && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  fill="currentColor"
                />
              </svg>
            )}
            {submitStatus.type === "error" && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                  fill="currentColor"
                />
              </svg>
            )}
            <span>{submitStatus.message}</span>
          </div>
        )}

        {!showOTP ? (
          <form className="seller-reg-form" onSubmit={handleSubmit}>
            <div className="seller-reg-grid">
              <div className="seller-reg-row">
                <div
                  className={`seller-reg-field full-width ${focusedField === "fullName" ? "seller-focused" : ""} ${form.fullName ? "seller-filled" : ""}`}
                >
                  <label className="seller-reg-label">
                    <span className="seller-label-text">
                      Full Name<span className="seller-required-star">*</span>
                    </span>
                  </label>
                  <div className="seller-input-wrapper">
                    <svg
                      className="seller-field-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                        fill="currentColor"
                      />
                    </svg>
                    <input
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      onFocus={() => handleFocus("fullName")}
                      onBlur={handleBlur}
                      placeholder="Enter your full name"
                      required
                      disabled={isSubmitted}
                    />
                  </div>
                </div>

                <div
                  className={`seller-reg-field full-width ${focusedField === "email" ? "seller-focused" : ""} ${form.email ? "seller-filled" : ""}`}
                >
                  <label className="seller-reg-label">
                    <span className="seller-label-text">
                      Email Address
                      <span className="seller-required-star">*</span>
                    </span>
                  </label>
                  <div className="seller-input-wrapper">
                    <svg
                      className="seller-field-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                        fill="currentColor"
                      />
                    </svg>
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      onFocus={() => handleFocus("email")}
                      onBlur={handleBlur}
                      placeholder="Enter your email"
                      type="email"
                      required
                      disabled={isSubmitted}
                    />
                  </div>
                </div>
              </div>

              <div className="seller-reg-row">
                <div
                  className={`seller-reg-field full-width ${focusedField === "phone" ? "seller-focused" : ""} ${form.phone ? "seller-filled" : ""}`}
                >
                  <label className="seller-reg-label">
                    <span className="seller-label-text">
                      Phone Number
                      <span className="seller-required-star">*</span>
                    </span>
                  </label>
                  <div className="seller-input-wrapper">
                    <svg
                      className="seller-field-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                        fill="currentColor"
                      />
                    </svg>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      onFocus={() => handleFocus("phone")}
                      onBlur={handleBlur}
                      placeholder="Enter your phone number"
                      type="tel"
                      required
                      disabled={isSubmitted}
                    />
                  </div>
                </div>

                <div
                  className={`seller-reg-field full-width ${focusedField === "businessName" ? "seller-focused" : ""} ${form.businessName ? "seller-filled" : ""}`}
                >
                  <label className="seller-reg-label">
                    <span className="seller-label-text">
                      Business Name
                      <span className="seller-required-star">*</span>
                    </span>
                  </label>
                  <div className="seller-input-wrapper">
                    <svg
                      className="seller-field-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4zm10 16H4V8h16v12z"
                        fill="currentColor"
                      />
                    </svg>
                    <input
                      name="businessName"
                      value={form.businessName}
                      onChange={handleChange}
                      onFocus={() => handleFocus("businessName")}
                      onBlur={handleBlur}
                      placeholder="Enter your business name"
                      required
                      disabled={isSubmitted}
                    />
                  </div>
                </div>
              </div>

              <div className="seller-reg-row">
                <div
                  className={`seller-reg-field full-width ${focusedField === "password" ? "seller-focused" : ""} ${form.password ? "seller-filled" : ""}`}
                >
                  <label className="seller-reg-label">
                    <span className="seller-label-text">
                      Password<span className="seller-required-star">*</span>
                    </span>
                  </label>
                  <div className="seller-input-wrapper">
                    <svg
                      className="seller-field-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
                        fill="currentColor"
                      />
                    </svg>
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={handleChange}
                      onFocus={() => handleFocus("password")}
                      onBlur={handleBlur}
                      placeholder="Enter your password"
                      required
                      disabled={isSubmitted}
                    />
                    <button
                      type="button"
                      className="seller-password-toggle"
                      onClick={() => setShowPassword((value) => !value)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        {showPassword ? (
                          <path
                            d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-4 .7l2.17 2.17C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                            fill="currentColor"
                          />
                        ) : (
                          <path
                            d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                            fill="currentColor"
                          />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>

                <div
                  className={`seller-reg-field full-width ${focusedField === "confirmPassword" ? "seller-focused" : ""} ${form.confirmPassword ? "seller-filled" : ""}`}
                >
                  <label className="seller-reg-label">
                    <span className="seller-label-text">
                      Confirm Password
                      <span className="seller-required-star">*</span>
                    </span>
                  </label>
                  <div className="seller-input-wrapper">
                    <svg
                      className="seller-field-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
                        fill="currentColor"
                      />
                    </svg>
                    <input
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={handleChange}
                      onFocus={() => handleFocus("confirmPassword")}
                      onBlur={handleBlur}
                      placeholder="Confirm your password"
                      required
                      disabled={isSubmitted}
                    />
                    <button
                      type="button"
                      className="seller-password-toggle"
                      onClick={() => setShowConfirmPassword((value) => !value)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        {showConfirmPassword ? (
                          <path
                            d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-4 .7l2.17 2.17C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                            fill="currentColor"
                          />
                        ) : (
                          <path
                            d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                            fill="currentColor"
                          />
                        )}
                      </svg>
                    </button>
                  </div>
                  {form.confirmPassword && (
                    <div className="seller-password-match">
                      {passwordMatch ? (
                        <span className="seller-match-success">
                          ✓ Passwords match
                        </span>
                      ) : (
                        <span className="seller-match-error">
                          ✕ Passwords do not match
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="seller-reg-footer">
              <button
                type="button"
                className="seller-cancel-btn"
                onClick={onClose}
                disabled={isSubmitted}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="seller-submit-btn"
                disabled={isSubmitted}
              >
                {isSubmitted ? (
                  <>
                    <span className="seller-spinner"></span>
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <span>Register & Get OTP</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z"
                        fill="currentColor"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="seller-otp-container">
            <div className="seller-otp-input-group">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`seller-otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="seller-otp-digit-input"
                  disabled={isVerifying}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <div className="seller-otp-timer">
              {otpTimer > 0 ? (
                <span>Resend OTP in {otpTimer}s</span>
              ) : (
                <button
                  className="seller-resend-otp-btn"
                  onClick={handleBackToRegistration}
                  disabled={!canResend}
                >
                  Resend OTP
                </button>
              )}
            </div>

            <div className="seller-otp-actions">
              <button
                onClick={handleBackToRegistration}
                className="seller-otp-back-btn"
                disabled={isVerifying}
              >
                Back
              </button>
              <button
                onClick={handleVerifyOtp}
                className="seller-otp-verify-btn"
                disabled={isVerifying || otp.join("").length !== 6}
              >
                {isVerifying ? (
                  <>
                    <span className="seller-otp-spinner"></span>
                    Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          </div>
        )}

        {!showOTP && (
          <p className="seller-reg-note">
            By registering, you agree to our Terms of Service and Privacy
            Policy. Our team will contact you within 24 hours.
          </p>
        )}
      </div>

      <Modal
        isOpen={showTermsModal}
        onClose={closeTermsModal}
        maxWidth="860px"
        ariaLabel="Seller Terms & Conditions"
      >
        <div className="modal-shell seller-terms-modal-shell">
          <div className="modal-header">
            <p className="modal-kicker">{sellerAgreementContent.modalTitle}</p>
            <h2 className="modal-title">{sellerAgreementContent.modalTitle}</h2>
            <p className="modal-subtitle">
              Please review the seller terms and conditions before continuing.
            </p>
          </div>

          <div className="modal-body">
            <div className="seller-terms-document-shell">
              <div className="seller-terms-instruction">
                Please review the seller terms and conditions and confirm your
                acceptance below to continue.
              </div>

              <div className="seller-terms-scroll">
                <div className="seller-terms-document">
                  <div className="seller-terms-header-block">
                    <h3 className="seller-terms-document-title">
                      {sellerAgreementContent.documentTitle}
                    </h3>
                    <p className="seller-terms-paragraph">
                      {sellerAgreementContent.intro}
                    </p>
                  </div>

                  {sellerAgreementContent.sections.map((section) => (
                    <section key={section.title} className="seller-terms-section">
                      <h4 className="seller-terms-section-title">{section.title}</h4>
                      {section.blocks.map((block, blockIndex) =>
                        renderAgreementBlock(block, section.title, blockIndex)
                      )}
                    </section>
                  ))}

                  <section className="seller-terms-section seller-terms-contact-block">
                    <h4 className="seller-terms-section-title">
                      {sellerAgreementContent.contact.title}
                    </h4>
                    {sellerAgreementContent.contact.blocks.map(
                      (block, blockIndex) =>
                        renderAgreementBlock(
                          block,
                          sellerAgreementContent.contact.title,
                          blockIndex,
                        ),
                    )}
                  </section>
                </div>
              </div>

              <div className="seller-terms-confirmation">
                <div className="seller-signature-pad-card">
                  <div className="seller-signature-pad-header">
                    <div>
                      <h4 className="seller-signature-pad-title">
                        Seller Signature
                      </h4>
                      <p className="seller-signature-pad-copy">
                        Sign inside the box below. This signature is required to
                        continue.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="seller-signature-pad-clear"
                      onClick={clearSignature}
                    >
                      Clear
                    </button>
                  </div>

                  <canvas
                    ref={signatureCanvasRef}
                    className={`seller-signature-pad ${sellerSignatureDataUrl ? "has-signature" : ""}`}
                    onPointerDown={startSignature}
                    onPointerMove={drawSignature}
                    onPointerUp={endSignature}
                    onPointerLeave={endSignature}
                  />

                  <div className="seller-signature-pad-status">
                    {sellerSignatureDataUrl
                      ? "Signature captured."
                      : "Draw your signature using mouse, touch, or stylus."}
                  </div>
                </div>

                <label
                  className={`seller-terms-checkbox ${hasAgreedSellerAgreement ? "is-enabled" : "is-disabled"}`}
                >
                  <input
                    type="checkbox"
                    checked={hasAgreedSellerAgreement}
                    onChange={(event) =>
                      setHasAgreedSellerAgreement(event.target.checked)
                    }
                  />
                  <span style={{ color: "black" }}>
                    I have read and agree to the Seller Terms & Conditions.
                  </span>
                </label>

                {(!hasAgreedSellerAgreement || !sellerSignatureDataUrl) && (
                  <p className="seller-terms-helper">
                    {!sellerSignatureDataUrl
                      ? "Add your signature and check the box to enable Accept Agreement."
                      : "Check the box to enable Accept Agreement."}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-button modal-button--secondary"
              onClick={closeTermsModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="modal-button modal-button--primary"
              onClick={handleAcceptTerms}
              disabled={!hasAgreedSellerAgreement || !sellerSignatureDataUrl || isSubmitted}
            >
              Accept Agreement
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
